import { ContentWrapper } from './styled';
import type { PageType } from '@/pages/DiscoverPageV2/constants';

type CriteriaType =
  | 'High Yield'
  | 'Consistent Dividends'
  | 'Dividend Cover'
  | 'Healthy Balance Sheet'
  | 'Profitability'
  | 'Low Cost Producer'
  | 'Growth';

const dividendCriteria = {
  'High Yield': {
    why: [
      'Companies with a high dividend yield are more attractive due to the higher expected income for each dollar invested.',
      'Yields vary between markets, so we focus on the top dividend payers in each market.',
    ],
    what: ["Is the yield in the top 25% of the market's dividend payers."],
  },
  'Consistent Dividends': {
    why: [
      'Companies with a strong track record of paying a consistent and growing dividend are the most attractive.',
      "If the dividend has been cut substantially in the past, then it's difficult to be confident about future payments.",
    ],
    what: [
      'Has the dividend been stable over the last 10 years.',
      'Has the dividend grown over the last 10 years.',
    ],
  },
  'Dividend Cover': {
    why: [
      "Ideally the company doesn't pay out all of its earnings, neglecting future growth.",
      'If a company is unable to afford its dividend, then it will probably lead to a dividend cut and share price erosion.',
    ],
    what: [
      'Are dividends covered by earnings.',
      'Are dividends forecast to be covered by earnings in the future.',
    ],
  },
  'Healthy Balance Sheet': {
    why: [
      'Investors want to make sure the company is positioned to cover its debts. Repayments on debt typically take priority over shareholder return initiatives.',
    ],
    what: [
      'Does the company have a manageable level of debt.',
      'Is the company able to cover its interest repayments.',
    ],
  },
};

const goldCriteria = {
  'Healthy Balance Sheet': {
    why: [
      'A healthy balance sheet is essential for resources companies to fund their intensive capital requirements.',
      'Repayments on debt take precedence over other initiatives to improve shareholder returns, so investors want to make sure the company is comfortably positioned to cover its debts.',
    ],
    what: [
      'Does the company have a manageable level of debt.',
      'Is the company able to cover its interest repayments.',
    ],
  },
  'Low Cost Producer': {
    why: [
      'Being a low cost producer is important because it protects the business in case the price of gold falls.',
      'Having strong returns on assets is a sign that they are being used efficiently and provide insulation against falling sales prices per unit.',
    ],
    what: [
      'Companies with a high return on assets (ROA) compared to their peers.',
    ],
  },
  Profitability: {
    why: [
      'In order to grow, companies will need to be able to expand the capacity of existing assets or develop new assets.',
      'Having sufficient income allows the company to maintain operations while considering expansion opportunities.',
    ],
    what: [
      'Is the company currently profitable or forecast to become profitable in 3 years.',
    ],
  },
};

const growthCriteria = {
  'Healthy Balance Sheet': {
    why: [
      'A healthy balance sheet is essential to drive growth opportunities and sustain the business.',
      'Repayments on debt take precedence over other initiatives to improve shareholder returns, so investors want to make sure the company is comfortably positioned to cover its debts.',
    ],
    what: [
      'Does the company have a manageable level of debt.',
      'Is the company able to cover its interest repayments.',
    ],
  },
  Growth: {
    why: [
      'Companies with sustained revenue growth that outperforms the market are attractive to investors. These companies are most likely to appreciate in share price over time.',
    ],
    what: ['Is the company forecast to have high earnings growth.'],
  },
};

const getCriteria = (type: PageType) => {
  switch (type) {
    case 'Dividend':
      return dividendCriteria;
    case 'Gold':
      return goldCriteria;
    case 'Growth':
      return growthCriteria;
  }
};

type Props = {
  pageType: PageType;
  criteriaType: CriteriaType;
};

const CriteriaDetails = ({ pageType, criteriaType }: Props) => {
  const criteria = getCriteria(pageType);
  const why = criteria[criteriaType].why;
  const what = criteria[criteriaType].what;
  return (
    <ContentWrapper data-cy-id={`criteria-${pageType}`}>
      <ul>
        {why.map((n, i) => (
          <li key={`why-${i}`}>{n}</li>
        ))}
      </ul>
      <h3>What do we look for?</h3>
      <ul>
        {what.map((n, i) => (
          <li key={`what-${i}`}>{n}</li>
        ))}
      </ul>
    </ContentWrapper>
  );
};

export default CriteriaDetails;
